<script setup lang="ts">
import { computed, ref, toRefs, watch } from "vue";
import { useI18n } from "vue-i18n";

import { useBackendSearch } from "@/components/search/useBackendSearch";
import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import IconButton from "@/components/ui/IconButton/IconButton.vue";
import InfoMarker from "@/components/ui/InfoMarker/InfoMarker.vue";
import MenuList from "@/components/ui/MenuList/MenuList.vue";
import MenuListFooter from "@/components/ui/MenuList/MenuListFooter.vue";
import MenuListHeader from "@/components/ui/MenuList/MenuListHeader.vue";
import MenuListItem from "@/components/ui/MenuList/MenuListItem.vue";
import MenuListItems from "@/components/ui/MenuList/MenuListItems.vue";
import MenuListSearch from "@/components/ui/MenuList/MenuListSearch.vue";
import MenuListSubheader from "@/components/ui/MenuList/MenuListSubheader.vue";
import MenuListTitle from "@/components/ui/MenuList/MenuListTitle.vue";
import TypeDot from "@/components/ui/TypeDot/TypeDot.vue";
import { isOrigin } from "@/model/search";
import { useBoardsStore } from "@/store/boards";
import type { LinkedToItem } from "@/store/issueSelector";
import { useStickyTypeStore } from "@/store/stickyType";
import { useTeamStore } from "@/store/team";

import IssueLinkedToTooltip from "./IssueLinkedToTooltip.vue";

const { t } = useI18n();

const props = defineProps<{
  selected: LinkedToItem[];
}>();

const emit = defineEmits<{
  change: [items: LinkedToItem[]];
}>();

// Search query ref
const searchQuery = ref("");

// Internal state for multi-select UX
// Store full LinkedToItem objects so they remain available even if not in current search results
const internalSelected = ref<Map<string, LinkedToItem>>(new Map());

// Sync internal state when props change
watch(
  () => props.selected,
  (selectedItems) => {
    const newSelected = new Map<string, LinkedToItem>();

    for (const item of selectedItems) {
      newSelected.set(item.almId, item);
    }

    internalSelected.value = newSelected;
  },
  { immediate: true },
);

/**
 * Search query for work items
 * - When NOT searching: current team's backlog board only
 * - When searching: text search across all boards (no board filter), then filter to backlog items client-side
 */
const searchItemsQuery = computed(() => {
  const artBacklogTypes = useStickyTypeStore().stickyTypes.filter(
    (type) => type.functionality === "workitem" && type.origin === "backlog",
  );

  const boardsStore = useBoardsStore();
  const isSearching = searchQuery.value.length > 2; // Start searching from 3 characters

  if (isSearching) {
    // Search all boards (backend handles text search index)
    // Filter to backlog items via types - no board filter needed
    return { text: searchQuery.value, types: artBacklogTypes };
  } else {
    // Default: current team's backlog board only
    return {
      teams: [useTeamStore().current],
      types: artBacklogTypes,
      boards: [boardsStore.boardByType("backlog")],
    };
  }
});

/**
 * Use backend search to fetch work items
 */
const { backendResults } = toRefs(useBackendSearch(searchItemsQuery));

/**
 * Transform search results to display format with team info
 */
const artWorkItems = computed<LinkedToItem[]>(() => {
  return backendResults.value.flatMap((result) => {
    if (!result.almId) return [];

    // Deduplicate results by only showing the origin card
    // (it's easier to filter them out here, since not all boards
    // are loaded in a ST session so we can't just filter by board)
    if (!isOrigin(result)) return [];

    const stickyType = useStickyTypeStore().findStickyType(result.typeId);
    const team = useTeamStore().teams.find((t) => t.id === result.teamId);

    return {
      id: result.id,
      almId: result.almId,
      text: result.text,
      type: { color: stickyType.color },
      teamId: result.teamId,
      teamName: team?.name || t("general.unassigned"),
    };
  });
});

/**
 * Selected items that are NOT in current search results (displayed at top)
 */
const missingSelectedItems = computed(() =>
  [...internalSelected.value.values()].filter(
    (item) => !artWorkItems.value.some((workItem) => workItem.almId === item.almId),
  ),
);

/**
 * Group all result items by team name (includes both selected and unselected)
 * Also prepends missing selected items as a special group at the top
 */
const groupedByTeamName = computed(() => {
  const grouped: Record<string, typeof artWorkItems.value> = {};

  const allItems = [...missingSelectedItems.value, ...artWorkItems.value];

  // Group search results by team
  for (const item of allItems) {
    const teamName = item.teamName;
    if (!grouped[teamName]) {
      grouped[teamName] = [];
    }
    grouped[teamName].push(item);
  }
  return { sortedEntries: Object.entries(grouped), noTeamFeatures: [] };
});

const hasActiveFilter = computed(() => internalSelected.value.size > 0);

/**
 * Check if work item is selected
 */
function isSelected(workItem: { almId?: string | null }): boolean {
  return workItem.almId ? internalSelected.value.has(workItem.almId) : false;
}

/**
 * Toggle work item selection
 */
function toggleWorkItem(workItem: LinkedToItem) {
  if (!workItem.almId) return;

  if (isSelected(workItem)) {
    internalSelected.value.delete(workItem.almId);
  } else {
    internalSelected.value.set(workItem.almId, workItem);
  }
}

/**
 * Clear all selections
 */
function clearAll() {
  internalSelected.value = new Map();
}

/**
 * Only emit if selection changed
 */
const handleClose = () => {
  const prevIds = new Set(props.selected.map((item) => item.almId || ""));
  const newIds = new Set(internalSelected.value.keys());

  if (prevIds.symmetricDifference(newIds).size > 0) {
    // Emit array of selected items (full objects, not just IDs)
    emit("change", [...internalSelected.value.values()]);
  }

  // Reset search query
  searchQuery.value = "";
};
</script>

<template>
  <DropdownMenu
    class="issue-linkedto-filter"
    disable-teleport
    stay-open-on-content-click
    width="lg"
    @close="handleClose"
  >
    <!-- Trigger -->
    <template #trigger="{ isOpen }">
      <IconButton
        icon="general/link"
        variant="outlined"
        size="small"
        :activated="isOpen"
        :aria-expanded="isOpen"
        aria-haspopup="listbox"
        :aria-label="$t('label.search.filterButton', { value: $t('issueSelector.linkedTo') })"
      >
        <template #tooltip>
          <IssueLinkedToTooltip :items="props.selected" />
        </template>
      </IconButton>
      <InfoMarker v-if="hasActiveFilter" :top="4" :right="4" />
    </template>

    <MenuList selectable multiselect>
      <MenuListHeader>
        <MenuListTitle>{{ $t("issueSelector.linkedTo") }}</MenuListTitle>
        <MenuListSearch :placeholder="$t('action.search')" @search="searchQuery = $event" />
      </MenuListHeader>

      <MenuListItems v-focus-trap>
        <!-- All items grouped by team (includes missing selected items at top if any) -->
        <template v-for="[teamName, features] in groupedByTeamName.sortedEntries" :key="teamName">
          <MenuListSubheader>
            {{ teamName }}
          </MenuListSubheader>

          <MenuListItem
            v-for="feature in features"
            :key="feature.id"
            class="feature-item"
            :selected="isSelected(feature)"
            @click="toggleWorkItem(feature)"
          >
            <template #before>
              <TypeDot class="feature-dot" :color="feature.type.color" size="medium" />
            </template>
            {{ feature.text }}
          </MenuListItem>
        </template>

        <!-- No results message -->
        <MenuListItem v-if="!groupedByTeamName.sortedEntries.length" static>
          {{ $t("searchResults.noResults") }}
        </MenuListItem>
      </MenuListItems>

      <MenuListFooter :disabled="internalSelected.size === 0" @click="clearAll" />
    </MenuList>
  </DropdownMenu>
</template>

<style lang="scss" scoped>
.feature-item {
  margin-left: -2px;
}
</style>
